import { type FieldValues, type Path, type UseFormReturn } from 'react-hook-form';
import type { FieldSchema, FormField } from '@/types/FieldSchema.type';
import { TextInput } from './fields/TextInput';
import { SelectInput } from './fields/SelectInput';
import { DateInput } from './fields/DateInput';
import { AttachmentInput } from './fields/AttachmentInput';
import { Col, Row, Space, type ColProps } from 'antd';
import type { SpaceProps } from 'antd/lib';
import { CombinedTextInput } from './fields/CombinedTextInput';
import { CheckboxInput } from './fields/CheckboxInput';

export const FormBuilder = <T extends FieldValues>({
  fields,
  methods,
  spaceSize = 'middle',
  showErrorMessage = true,
  isResponsive = false,
  disabled = false,
  colProps,
}: {
  fields: FieldSchema<T>[];
  methods: UseFormReturn<T, any, FieldValues>; // bisa di-typing lebih spesifik jika perlu
  spaceSize?: SpaceProps['size'];
  showErrorMessage?: boolean;
  isResponsive?: boolean;
  colProps?: ColProps;
  disabled?: boolean;
}) => {
  const renderField = (field: FormField<T>) => {
    let isDisabled = disabled || field.disabled || false;

    if (field.dependsOn && field.disableIfEmpty) {
      const watchedValue = methods.watch(field.dependsOn as Path<T>);
      isDisabled = !watchedValue;
    }

    if (field.dependsOn && field.hideIfEmpty) {
      const watchedValue = methods.watch(field.dependsOn as Path<T>);
      if (!watchedValue) {
        return null;
      }
    }

    // Handle hidden prop - unconditionally hide the field
    if (field.hidden) {
      return null;
    }

    // Handle hiddenIfEmpty prop - hide the field if its value is empty
    if (field.hiddenIfEmpty) {
      const fieldValue = methods.watch(field.name as Path<T>);
      if (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0) || fieldValue === '') {
        return null;
      }
    }

    const commonProps = {
      info: field.info,
      name: field.name,
      label: field.label,
      placeholder: field.placeholder,
      disabled: isDisabled,
      required: field.required,
      multiple: field.multiple,
      allowSearch: field.allowSearch,
      loading: field.loading,
      onChange: field.onChange as any,
      onChangeCallback: field.onChangeCallback as any,
      onSearch: field.onSearch,
      validate: field.validate,
      prefix: field.prefix,
      suffix: field.suffix,
      showErrorMessage,
      variant: field.variant,
      autoComplete: field.autoComplete,
    };

    switch (field.type) {
      case 'text':
      case 'number':
      case 'textarea':
      case 'password':
        return (
          <TextInput
            key={commonProps.name.toString()}
            {...commonProps}
            name={commonProps.name as keyof T & string}
            type={field.type}
          />
        );
      case 'select':
        return (
          <SelectInput
            key={commonProps.name.toString()}
            {...commonProps}
            name={commonProps.name as keyof T & string}
            options={field.options || []}
            apiUrl={field.apiUrl}
            apiParams={field.apiParams}
            searchKey={field.searchKey}
            allowClear={field.allowClear}
            renderOption={field.renderOption}
          />
        );
      case 'checkbox':
        return (
          <CheckboxInput
            key={commonProps.name}
            {...commonProps}
            options={field.options || []}
            mutuallyExclusiveValue={field.mutuallyExclusiveValue}
          />
        );
      case 'date':
        return <DateInput key={commonProps.name} {...commonProps} />;
      case 'combined-text':
        return <CombinedTextInput key={commonProps.name} {...commonProps} fields={field.fields || []} />;
      case 'attachment':
        return (
          <AttachmentInput
            key={commonProps.name.toString()}
            {...commonProps}
            name={commonProps.name as keyof T & string}
            maxConcurrentUploads={field.maxConcurrentUploads}
            onUploadComplete={field.onUploadComplete}
            onUploadError={field.onUploadError}
          />
        );
      default:
        return null;
    }
  };

  if (isResponsive) {
    return (
      <Row gutter={[8, 8]}>
        {fields.map(field => (
          <Col key={field.name} {...(field.colProps || colProps)}>
            {renderField(field)}
          </Col>
        ))}
      </Row>
    );
  }

  return (
    <Space direction="vertical" size={spaceSize} className="w-full">
      {fields.map(renderField)}
    </Space>
  );
};
